import { useRef } from 'react';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import { StripeElementStyle } from '@stripe/stripe-js';
import { Control, Controller, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { clsx } from 'clsx';
import { PaymentMethodFormValues } from './paymentMethodFormValues';

const CARD_ELEMENT_OPTIONS: { className: string; style: StripeElementStyle } = {
  className: 'testClass',
  style: {
    base: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#05050DCC',
      letterSpacing: '0.025em',
      '::placeholder': {
        color: '#B8C1C6',
      },
    },
    invalid: {
      color: '#FF5151',
    },
  },
};

const STRIPE_ELEMENTS = {
  card_number: CardNumberElement,
  expiration: CardExpiryElement,
  cvc: CardCvcElement,
};

export type StripeCardFieldName = keyof typeof STRIPE_ELEMENTS;

interface StripeCardFieldProps {
  name: StripeCardFieldName;
  label: string;
  control: Control<PaymentMethodFormValues>;
  setError: UseFormSetError<PaymentMethodFormValues>;
  clearErrors: UseFormClearErrors<PaymentMethodFormValues>;
  errorMessage?: string;
  className?: string;
  fieldClassName?: string;
}

/**
 * A Stripe card Element bound to a react-hook-form field. The form value mirrors Stripe's
 * `{ complete, error }` change payload because Stripe never exposes the raw card data.
 */
export const StripeCardField = ({
  name,
  label,
  control,
  setError,
  clearErrors,
  errorMessage,
  className,
  fieldClassName,
}: StripeCardFieldProps) => {
  const fieldRef = useRef<HTMLDivElement>(null);
  const StripeElement = STRIPE_ELEMENTS[name];

  return (
    <label className={clsx('text-grey_450', className)}>
      {label}
      <div
        ref={fieldRef}
        className={clsx(
          'rounded-sm-5 border transition duration-300 px-3 bg-white relative',
          fieldClassName,
          errorMessage && 'border-error',
        )}
      >
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange } }) => (
            <>
              <StripeElement
                options={CARD_ELEMENT_OPTIONS}
                onReady={() => {
                  clearErrors(name);
                }}
                onChange={(event) => {
                  onChange({
                    complete: event?.complete,
                    error: {
                      message: event?.error?.message,
                    },
                  });
                  if (event?.error?.message) {
                    setError(name, {
                      message: event?.error?.message,
                    });
                  }
                }}
                onBlur={() => {
                  fieldRef.current?.classList.remove(
                    'border-green_750',
                    '[&_.error-message]:hidden',
                  );
                }}
                onFocus={() => {
                  fieldRef.current?.classList.add('border-green_750', '[&_.error-message]:hidden');
                }}
              />
              {errorMessage && (
                <div className="z-10 max-md:hidden error-message absolute flex items-center justify-center px-2.5 py-2 w-full left-0 -bottom-1 transform translate-y-[100%] bg-error_bg rounded-sm-5 overflow-hidden">
                  <p className="text-sm font-semibold text-error">{errorMessage}</p>
                </div>
              )}
            </>
          )}
        />
      </div>
    </label>
  );
};
